import React from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import dateUtils from '../../../util/dates';
import math from '../../../util/math';
import { getDetailsData } from '../ThroughputHistogramActions';

import highchartFactory from '../../../util/highcharts';
import chartUtils from '../../../util/chartUtils';
const highchartUtils = highchartFactory(Highcharts);

const PERIOD_NAMES = { day: 'day', week: 'week', '2weeks': 'fortnight', month: 'month' };
// the chart and the navigator under it start where the longest period labels end ("100 fortnights" at 12px)
const PLOT_LEFT = 96;

const chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'column',
    zoomType: 'xy',
    resetZoomButton: chartUtils.RESET_ZOOM_BESIDE_BUTTONS,
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: PLOT_LEFT,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  xAxis: {
    type: 'linear',
    lineColor: chartUtils.TREND_AXIS_LINE_COLOR,
    tickColor: chartUtils.TREND_AXIS_LINE_COLOR,
    labels: {
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px'
      }
    },
    title: {
      text: 'Throughput (number of work items completed on a day)',
      style: {
        fontSize: '13px'
      }
    },
    plotLines: [],
    events: {}
  },
  yAxis: {
    allowDecimals: false,
    labels: {
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px',
        whiteSpace: 'nowrap'
      }
    },
    min: 0,
    gridLineColor: chartUtils.TREND_GRID_COLOR,
    gridLineWidth: 1,
    title: {
      text: null
    }
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      stickyTracking: false,
      color: chartUtils.TREND_LINE_COLOR
    },
    column: {
      pointPadding: 0.06,
      borderWidth: 0,
      groupPadding: 0,
      shadow: false,
      inactiveOtherPoints: true,
      states: chartUtils.BAR_STATES
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0
  },
  series: [
    {
      name: 'Data',
      data: []
    }
  ]
};
const masterchartConfig = {
  credits: {
    enabled: false
  },
  exporting: {
    enabled: false
  },
  chart: {
    reflow: false,
    borderWidth: 0,
    backgroundColor: null,
    marginLeft: PLOT_LEFT,
    marginRight: 10,
    zoomType: 'x',
    events: {}
  },
  title: {
    text: ''
  },
  xAxis: {
    type: 'datetime',
    ordinal: false,
    showLastTickLabel: true,
    plotBands: [
      {
        id: 'mask-before',
        color: chartUtils.ZOOM_MASK_COLOR
      }
    ],
    title: {
      text: null
    },
    labels: {
      style: {
        fontSize: '12px'
      }
    }
  },
  yAxis: {
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Daily throughput',
      style: {
        fontSize: '13px'
      }
    },
    labels: {
      style: {
        fontSize: '12px'
      }
    }
  },
  plotOptions: {
    series: {
      lineWidth: 1.5,
      marker: {
        enabled: false
      },
      shadow: false,
      states: {
        hover: {
          lineWidth: 1
        }
      },
      enableMouseTracking: false
    }
  },
  tooltip: {
    formatter: function() {
      return false;
    }
  },
  legend: {
    enabled: false
  },
  series: [
    {
      name: 'Data',
      color: '#7CB5EC',
      type: 'spline',
      data: []
    }
  ]
};

export class ThroughputHistogramChart extends React.Component {
  constructor(props) {
    super(props);
    this.chartContainer = React.createRef();
    this.masterContainer = React.createRef();
    this.state = {};

    this.onSelection = this.onSelection.bind(this);
    this.updateDetails = this.updateDetails.bind(this);
  }

  calculatePercentiles = data => {
    const lines = [],
      ticks = [];

    this.calcMeanModeMedian(lines, ticks, data);

    for (let i = 0; i < this.props.percentiles.length; i++) {
      const pObj = _.find(data.percentiles, item => item.name == this.props.percentiles[i]);
      if (!pObj) {
        continue;
      }

      const text = `${pObj.name}%`;
      lines.push(chartUtils.percentileLine(pObj.name, +pObj.value, highchartUtils.getFontSizeConfig('percentile')));
      this.addTick(ticks, text, pObj.value);
    }

    return chartUtils.groupReferenceLines(lines, ticks, '', highchartUtils.plotlinesTooltip);
  };

  calcMeanModeMedian(lines, ticks, data) {
    if (!data.series || !data.series.length) {
      return false;
    }

    const mode = math.getModeFromHistogramBy(data.series, 'y', 'x');
    const mean = math.getMeanFromHistogramBy(data.series, 'y', 'x');
    const median = math.getMedianFromHistogramBy(data.series, 'y', 'x');

    this.addLine(lines, 'Mean', mean.toFixed(2));
    this.addLine(lines, 'Median', median);
    this.addLine(lines, 'Mode', mode);

    this.addTick(ticks, 'Mean', mean.toFixed(2));
    this.addTick(ticks, 'Median', median);
    this.addTick(ticks, 'Mode', mode);
  }

  addLine(lines, text, val) {
    lines.push({ ...chartUtils.referenceLine(text, +val, highchartUtils.getFontSizeConfig('percentile')), color: chartUtils.STAT_LINE_COLORS[text] });
  }

  placeLineLabels(chart) {
    chartUtils.placeTopLineLabels(chart, this.props.wallmode ? 10 : 34);
  }

  addTick(ticks, text, value) {
    ticks.push(chartUtils.hoverLine(text, +value));
  }

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    chartConfig.exporting = this.props.chartExporting;
    masterchartConfig.chart.events.selection = this.onSelection;
    const that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(this);
    };
    chartConfig.chart.events.render = function() {
      that.placeLineLabels(this);
      chartUtils.roundTops(this);
    };

    this.master = new Highcharts['Chart'](this.masterContainer.current, masterchartConfig);
    this.chart = new Highcharts['Chart'](this.chartContainer.current, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);

    if (this.props.data && this.props.data.length) {
      this.updateChart();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.updateChart();
    }

    if (prevProps.details != this.props.details) {
      this.updateDetails(this.props.details);
    }

    if (prevProps.timeUnit !== this.props.timeUnit) {
      this.props.getDetailsData(this.props.dates);
    }

    if (prevProps.percentiles != this.props.percentiles) {
      this.chart.update({
        xAxis: {
          plotLines: this.calculatePercentiles(this.props.details)
        }
      });
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (prevProps.showNavigator != this.props.showNavigator) {
      this.master.reflow();
      this.chart.reflow();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename,
            chartOptions: {
              title: {
                text: 'Throughput Histogram',
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              },          
            }
          }
        },
        false
      );
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
    this.master.destroy();
  }

  updateChart() {
    this.master.update({
      xAxis: {
        plotBands: [
          {
            from: this.props.data.length ? this.props.data[0].x : 0,
            to: this.props.data.length ? this.props.data[this.props.data.length - 1].x : 0
          }
        ]
      },
      series: [
        {
          data: _(this.props.data)
            .flatMap('data')
            .sortBy('x')
            .value()
        }
      ]
    });

    if (this.props.details.series) {
      this.updateDetails(this.props.details);
    }
  }

  updateTimeLabelsFormat(dateFormat) {
    const config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.master.update(config);
  }

  // the counted periods' unit sits in the labels: "5 days", "1 fortnight"
  periodLabels() {
    const period = PERIOD_NAMES[this.props.timeUnit];
    return {
      labels: {
        formatter: function() {
          return chartUtils.withUnit(this.value, period);
        }
      }
    };
  }

  tooltipFormatter(ev) {
    if (ev.key == 'Plotline') {
      return `${ev.point.text}: ${ev.y}`;
    }
    return this.props.wallmode
      ? chartUtils.tvTooltipLead(`Frequency: ${ev.y}`, `Throughput: ${ev.x}`)
      : `<p class="tooltip-label-size">Throughput: ${ev.x}</p><p class="tooltip-label-size">Frequency: ${ev.y}</p>`;
  }

  updateDetails(data) {
    if (this.props.data.length) {
      let xText;
      switch (this.props.timeUnit) {
        case '2weeks': {
          xText = `Throughput (number of work items completed on 2 weeks)`;
          break;
        }
        default: {
          xText = `Throughput (number of work items completed on a ${this.props.timeUnit})`;
          break;
        }
      }

      this.chart.update(
        {
          xAxis: {
            title: {
              text: xText
            },
            plotLines: this.calculatePercentiles(data)
          },
          yAxis: this.periodLabels(),
          series: [
            {
              data: data.series
            }
          ]
        },
        true,
        false,
        false
      );
    } else {
      this.chart.update({
        series: [
          {
            data: []
          }
        ]
      });
    }
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('th');
    this.chart.update({
      ...newConfig.chart,
      // the TV's larger axis labels take the room they need
      chart: { marginLeft: null }
    });
    this.master.update(newConfig.master);
  }

  onSelection(e) {
    var extremesObject = e.xAxis[0],
      min = extremesObject.min,
      max = extremesObject.max,
      xAxis = e.target.xAxis[0];

    xAxis.removePlotBand('mask-before');
    xAxis.addPlotBand({
      id: 'mask-before',
      from: xAxis.dataMin,
      to: min,
      color: chartUtils.ZOOM_MASK_COLOR,
      borderColor: chartUtils.ZOOM_MASK_BORDER,
      borderWidth: 1
    });

    xAxis.removePlotBand('mask-after');
    xAxis.addPlotBand({
      id: 'mask-after',
      from: max,
      to: xAxis.dataMax,
      color: chartUtils.ZOOM_MASK_COLOR,
      borderColor: chartUtils.ZOOM_MASK_BORDER,
      borderWidth: 1
    });

    if (this.master.resetBtn) {
      this.master.resetBtn.destroy();
    }
    this.master.resetBtn = this.master.renderer
      .button(
        'Reset zoom',
        null,
        null,
        () => {
          this.resetExtremes();
        },
        chartUtils.RESET_ZOOM_THEME,
        chartUtils.RESET_ZOOM_HOVER
      )
      .attr({
        align: 'right',
        title: 'Reset zoom level 1:1'
      })
      .addClass('highcharts-reset-zoom')
      .add(this.master.zoomGroupButton)
      .align(
        {
          align: 'right',
          x: -8,
          y: 8
        },
        false,
        null
      );

    const from = moment(min),
      to = moment(max);
    this.props.getDetailsData({
      startDate: from,
      endDate: to
    });

    return false;
  }

  resetExtremes() {
    this.master.xAxis[0].removePlotBand('mask-before');
    this.master.xAxis[0].removePlotBand('mask-after');
    this.props.getDetailsData(this.props.dates);
    this.master.resetBtn.destroy();
    delete this.master.resetBtn;
  }

  render() {
    return (
      <div className="chart_content chart_tooltip_style">
        <div
          ref={this.chartContainer}
          className={'chart_details ' + (!this.props.showNavigator ? 'chart_details-full ' : ' ')}
        ></div>
        <div
          style={{ display: this.props.showNavigator ? 'block' : 'none' }}
          ref={this.masterContainer}
          className="chart_master"
        ></div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    getDetailsData: dates => dispatch(getDetailsData(dates))
  };
};

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.th.dateFormat,
    percentiles: state.filters.th.selectedPercentiles,
    showNavigator: state.filters.th.showNavigator,
    timeUnit: state.filters.th.timeUnit,
    details: state.throughputHistogram.details,
    wallmode: state.app.wallmode
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(ThroughputHistogramChart));
